import {
  WSElement,
  WSElementProps,
  WSInfiniteScroll,
  WSTable,
  WSTableItem,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useCallback, useState } from "react";
import { TaxFormStatusBadge } from "../components/TaxFormStatusBadge";
import { TaxID } from "../components/TaxID";

import { useHistory, useLocation } from "react-router-dom";
import {
  Form1099Status,
  ICollaboratorSchema
} from "@wingspanhq/payments/dist/interfaces";
import { getCurrentYearForm1099Balances } from "../../query/payments/selectors";
import { getBusinessTypeLabel } from "../../Settings/components/SettingsUpdateForms/BusinessTypeForm";
import getCompanyStructure from "../utils/getCompanyStructure";
import getTin from "../utils/getTin";
import getCollaboratorName from "../utils/getCollaboratorName";

export interface NEC1099FilingTableProps extends WSElementProps {
  collaborators: ICollaboratorSchema[];
}

const PAGE_SIZE = 20;

export const NEC1099FilingTable: React.FC<NEC1099FilingTableProps> = ({
  collaborators,
  ...elementProps
}) => {
  const history = useHistory();
  const location = useLocation();

  const [page, setPage] = useState(0);

  const onView = useCallback(
    (item: WSTableItem) => {
      history.push(`${location.pathname}/${item.id}`);
    },
    [location.pathname, history]
  );

  return (
    <WSElement {...elementProps}>
      <WSInfiniteScroll
        onLoad={() => {
          setPage(p => ++p);
        }}
        loadMore={collaborators.length > page * PAGE_SIZE}
        endOfList={collaborators.length <= page * PAGE_SIZE}
        loading={false}
      >
        <WSTable
          showArrow
          onRowClick={onView}
          columns={[
            {
              config: {
                gridTemplateSizeMax: "1fr",
                header: {
                  text: "Name"
                }
              },
              renderFunction: ({ data: collaborator }) => (
                <WSText>{getCollaboratorName(collaborator)}</WSText>
              )
            },
            {
              config: {
                gridTemplateSizeMax: "1fr",
                header: {
                  text: "Tax ID"
                }
              },
              renderFunction: ({ data }) => (
                <TaxID taxId={getTin(data)} taxStatus={data.taxStatus} />
              )
            },
            {
              config: {
                gridTemplateSizeMax: "1fr",
                header: {
                  text: "Company Type"
                }
              },
              renderFunction: ({ data }) => (
                <WSText color="grey700">
                  {getBusinessTypeLabel(getCompanyStructure(data)) || "-"}
                </WSText>
              )
            },
            {
              config: {
                gridTemplateSizeMax: "1fr",
                header: {
                  text: "Filing Status"
                }
              },
              renderFunction: ({ data }) => {
                const currentForm1099Balances = getCurrentYearForm1099Balances(
                  data
                );
                return (
                  <TaxFormStatusBadge
                    status={currentForm1099Balances?.status as Form1099Status}
                  />
                );
              }
            },
            {
              config: {
                gridTemplateSizeMax: "1fr",
                header: {
                  text: "1099-NEC Total"
                }
              },
              renderFunction: ({ data }) => {
                const currentForm1099Balances = getCurrentYearForm1099Balances(
                  data
                );
                let nec1099Total = 0;
                if (
                  currentForm1099Balances &&
                  currentForm1099Balances?.platformIncome >= 0
                ) {
                  nec1099Total = currentForm1099Balances?.platformIncome;
                }
                return (
                  <WSText color="grey700" formatMoney="default">
                    {nec1099Total}
                  </WSText>
                );
              }
            }
          ]}
          tableData={collaborators.slice(0, page * PAGE_SIZE).map(row => ({
            id: row.collaboratorId,
            data: row
          }))}
        />
      </WSInfiniteScroll>
    </WSElement>
  );
};
